import { useState } from 'react';
import { isImageUrl } from '@utils';

import { Icon as BlueIcon } from '@blueprintjs/core';
// import createFileList from 'create-file-list';

import * as yup from 'yup';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { patchEquipmentPicture } from '@services/queries/pictures/patchEquipmentPictures/patchEquipmentPictures';
import { beam } from '@styles/colors/themes';
import { PictureWithBlob } from '@services/queries/pictures/getPicture/getPicture';

import {
  Overlay,
  Container,
  RowDiv,
  Text,
  Button,
  ColumnDiv,
  Input,
  AddButton,
  FileName,
  IconCloseAwesome,
  SelectFile,
  Divider,
} from './PicturesEdit.styles';
import { PictureInputRow } from '../PicturesAdd/PicturesAdd.styles';

interface IPicturesEdit {
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
  picture: PictureWithBlob;
  setOpenMenu: (bool: boolean) => void;
}

interface IFormData {
  name: string;
  uri: string;
  type: 'uri' | 'file';
  file?: FileList;
}

const PicturesEdit = ({ setShowEdit, picture, setOpenMenu }: IPicturesEdit) => {
  const queryClient = useQueryClient();
  const params = useParams<{ equipmentId: string }>();

  const [pictureName, setPictureName] = useState<string>(picture.name);

  const [edited, setEdited] = useState<boolean>(false);
  const [fileEdited, setFileEdited] = useState<boolean>(false);
  const [uriEdited, setUriEdited] = useState<boolean>(false);
  const [pictureUri, setPictureUri] = useState<string>(
    picture.uri?.split('?')[0] || '',
  );
  const [fileName, setFileName] = useState<string>(
    picture.file?.path?.split('?')[0] || '',
  );

  const schema = yup
    .object({
      name: yup.string().required(),
      uri: yup.string(),
      file: yup.mixed(),
    })
    .required();

  const mutationEditPicture = useMutation(patchEquipmentPicture, {
    onSuccess: () => {
      setShowEdit(false);
      setOpenMenu(false);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'pictures',
      ]);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: picture.name,
      uri: picture.uri || '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: IFormData) => {
    const result: any = {
      id: picture.id,
      name: data.name,
    };
    if (pictureUri && uriEdited) {
      result.type = 'uri';
      result.uri = data.uri;
    }
    if (fileName && fileEdited) {
      result.type = 'file';
      result.file = data.file?.[0];
    }
    if (!mutationEditPicture.isLoading) {
      mutationEditPicture.mutate(result);
    }
  };

  const PictureOrigin = () => {
    if (pictureUri !== '')
      return (
        <Input
          type="text"
          placeholder="Upload an image url or select a file"
          {...register('uri')}
          isLink={isImageUrl(pictureUri)}
          onChange={e => {
            register('uri').onChange(e);
            setPictureUri(e.target.value);
            setUriEdited(true);
            setEdited(true);
          }}
          isError={!!errors.uri?.message}
        />
      );
    if (fileName !== '')
      return (
        <FileName>
          {fileName}
          <IconCloseAwesome
            icon={faTimes}
            onClick={() => {
              setFileName('');
              setValue('file', undefined);
            }}
          />
        </FileName>
      );
    return (
      <PictureInputRow>
        <Input
          type="text"
          placeholder="Image URL or Select a File"
          {...register('uri')}
          onChange={e => {
            register('uri').onChange(e);
            setPictureUri(e.target.value);
            setUriEdited(true);
            setEdited(true);
          }}
        />

        <SelectFile htmlFor="upload">Select File</SelectFile>
        <input
          type="file"
          id="upload"
          hidden
          accept="image/*"
          {...register('file')}
          onChange={e => {
            register('file').onChange(e);
            setFileName(e.target.files?.[0]?.name || '');
            setFileEdited(true);
            setEdited(true);
          }}
        />
      </PictureInputRow>
    );
  };

  return (
    <Overlay>
      <Container>
        <RowDiv
          style={{
            marginBottom: '16px',
            justifyContent: 'space-between',
            alignSelf: 'flex-start',
          }}
        >
          <Text className="title">Edit Photo</Text>

          <Button
            onClick={() => {
              setShowEdit(false);
              setOpenMenu(false);
            }}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </Button>
        </RowDiv>

        <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
          <ColumnDiv>
            <Text style={{ marginBottom: '8px' }}>Title</Text>
            <Input
              type="text"
              placeholder="Default"
              {...register('name')}
              onChange={e => {
                register('name').onChange(e);
                setPictureName(e.target.value);
                setEdited(true);
              }}
            />
            <div style={{ marginTop: '-17px', color: beam.font.error }}>
              {errors.name?.message}
            </div>

            <Text style={{ marginBottom: '0px', marginTop: '17px' }}>
              Picture
            </Text>

            <Divider height="8px" />

            {PictureOrigin()}

            <AddButton
              type="submit"
              disabled={
                !isValid ||
                !pictureName ||
                (!pictureUri && !fileName) ||
                !edited
              }
            >
              Save
            </AddButton>
          </ColumnDiv>
        </form>
      </Container>
    </Overlay>
  );
};

export { PicturesEdit };
